import { useQueryClient } from "@tanstack/react-query"
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatches,
  useNavigate,
} from "@tanstack/react-router"
import { ReserveSessionStatus } from "ipctg-inflow-types/InFlowEnums"
import { useCallback, useEffect, useState } from "react"

import api from "@/utilities/api"

import { matchesToRoutes, matchToRoute } from "@/utilities/etc"
import { ReserveSessionStatusError } from "@/utilities/expectedErrors"
import {
  reservePaymentIntentQueryOptions,
  reserveSetupIntentQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { ReservationHoldContext } from "@/utilities/hooks/useReservationHold"
import { log } from "@/utilities/logging"
import {
  isConcurrentSessionSentinel,
  isHoldExpired,
  redirectToConcurrentSession,
  redirectToReserveTimeout,
  RESERVE_TIMEOUT_ROUTE,
  secondsUntil,
} from "@/utilities/reserveHold"
import welcomeTokenStorage from "@/utilities/WelcomeTokenStorage"

/** How long before the deadline the cart starts showing it as a warning. */
const WARNING_THRESHOLD_SECONDS = 120

/** How often the countdown re-reads the clock. */
const HOLD_TICK_MS = 5000

/**
 * The pages that only work while the reservation is still purchasable. Rates is
 * left out on purpose — that's where the parker takes out a new hold.
 */
const GUARDED_ROUTES = [
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/vehicles",
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/cart/",
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/checkout",
] as const

export const Route = createFileRoute("/qr/$qrLookupID/reserve/$epoch")({
  beforeLoad: async ({ context, params, matches }) => {
    const isValidEpoch = welcomeTokenStorage.validateEpochFromUrl({
      epoch: params.epoch,
      flow: "reserve",
    })

    if (!isValidEpoch) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.epoch",
        message: "Invalid epoch, redirecting to /welcome",
      })
      throw redirect({ to: "/qr/$qrLookupID/welcome", params })
    }

    if (!context.isAuthenticated) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.auth",
        message: "User is unauthenticated, redirecting to /welcome",
      })
      throw redirect({ to: "/qr/$qrLookupID/welcome", params })
    }

    const sessionState = await api.reserve.sessionStatus.POST({})

    if ("failReason" in sessionState) {
      throw new ReserveSessionStatusError(sessionState.failReason)
    }

    if (
      sessionState.status === ReserveSessionStatus.Complete &&
      !matchesToRoutes({
        matches,
        paths: [
          "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/confirmation",
          "/qr/$qrLookupID/reserve/$epoch/payment-complete",
        ],
      })
    ) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.sessionState",
        message: "Session status is complete, redirecting to payment-complete",
        meta: { sessionState },
      })
      throw redirect({
        to: "/qr/$qrLookupID/reserve/$epoch/payment-complete",
        params: { qrLookupID: params.qrLookupID, epoch: params.epoch },
      })
    }

    /*
      Guarding here, before the child loads, means an expired hold never gets as
      far as POSTing /checkout or /payment-intent just to be told it's gone.
    */
    const guardedMatch = matchToRoute({ matches, paths: GUARDED_ROUTES })

    if (
      guardedMatch &&
      isConcurrentSessionSentinel(sessionState.holdReleaseTimestamp)
    ) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.hold",
        message:
          "Hold timestamp is the concurrent-session sentinel, redirecting to the in-progress page",
        meta: {
          routeId: guardedMatch.routeId,
          holdReleaseTimestamp: sessionState.holdReleaseTimestamp,
        },
      })
      redirectToConcurrentSession(guardedMatch.params)
    }

    if (guardedMatch && isHoldExpired(sessionState.holdReleaseTimestamp)) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.hold",
        message:
          "Reservation hold already expired, redirecting to the timeout page",
        meta: {
          routeId: guardedMatch.routeId,
          holdReleaseTimestamp: sessionState.holdReleaseTimestamp,
        },
      })
      redirectToReserveTimeout(guardedMatch.params)
    }

    return { sessionState }
  },
  component: RouteComponent,
})

/** The live half of the guard: the cart's countdown, and the lapsed redirect. */
function RouteComponent() {
  const {
    sessionState: { holdReleaseTimestamp },
  } = Route.useRouteContext()
  const navigate = useNavigate()
  const matches = useMatches()
  const queryClient = useQueryClient()

  // The dated params the timeout page needs live on the leaf, not on this route.
  const guardedMatch = matchToRoute({ matches, paths: GUARDED_ROUTES })
  const isOnGuardedRoute = guardedMatch !== undefined
  const timeoutParams = guardedMatch?.params

  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    secondsUntil(holdReleaseTimestamp)
  )
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false)

  /*
    Recompute from the timestamp rather than decrementing a counter, so a
    throttled interval in a backgrounded tab can't drift, and re-read on
    visibilitychange so a tab that slept through the deadline reads as expired
    on return instead of one tick later.
  */
  useEffect(() => {
    const readRemaining = () =>
      setSecondsRemaining(secondsUntil(holdReleaseTimestamp))

    readRemaining()

    // Confirmation and payment-complete carry a timestamp but nothing to guard.
    if (!isOnGuardedRoute) return

    const interval = setInterval(readRemaining, HOLD_TICK_MS)
    document.addEventListener("visibilitychange", readRemaining)
    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", readRemaining)
    }
  }, [holdReleaseTimestamp, isOnGuardedRoute])

  const isExpired = secondsRemaining !== null && secondsRemaining <= 0
  const isWarning =
    secondsRemaining !== null && secondsRemaining <= WARNING_THRESHOLD_SECONDS

  const checkExpiry = useCallback(
    () => isHoldExpired(holdReleaseTimestamp),
    [holdReleaseTimestamp]
  )

  const expireHold = useCallback(() => {
    /*
      Any clientSecret already fetched belongs to a hold that no longer exists,
      so coming back through checkout mints a fresh one.
    */
    queryClient.removeQueries(reservePaymentIntentQueryOptions())
    queryClient.removeQueries(reserveSetupIntentQueryOptions())

    if (!timeoutParams) {
      log({
        level: "warn",
        flow: "reserve",
        scope: "guard.hold",
        message:
          "Reservation expired outside a dated reserve route, cannot show the timeout page",
      })
      return
    }

    navigate({ to: RESERVE_TIMEOUT_ROUTE, params: timeoutParams })
  }, [navigate, queryClient, timeoutParams])

  useEffect(() => {
    if (!isExpired || !isOnGuardedRoute) return

    /*
      isExpired only wakes this up — it still describes the previous hold on the
      render a new one arrives, which would bounce a freshly taken rate.
    */
    if (!isHoldExpired(holdReleaseTimestamp)) return

    // A confirm in flight may already have charged the card, so let it finish.
    if (isConfirmingPayment) return

    log({
      level: "warn",
      flow: "reserve",
      scope: "guard.hold",
      message: "Reservation hold expired, redirecting to the timeout page",
      meta: { holdReleaseTimestamp },
    })
    expireHold()
  }, [
    expireHold,
    holdReleaseTimestamp,
    isConfirmingPayment,
    isExpired,
    isOnGuardedRoute,
  ])

  return (
    <ReservationHoldContext.Provider
      value={{
        isExpired,
        isWarning,
        checkExpiry,
        setIsConfirmingPayment,
        expireHold,
      }}
    >
      <Outlet />
    </ReservationHoldContext.Provider>
  )
}
